import { useState, useMemo, useCallback } from 'react'
import { studentsStore } from '../../../../../pages/Students/model/studentsStore'
import { useIsMobile } from 'shared/hooks/useBreakpoint'

export const useStepRecipients = ({
	selectedStudents = [],
	savedLists = [],
	activeGrade = 'all',
	onSelectList,
}) => {
	const [searchQuery, setSearchQuery] = useState('')
	const [showTableView, setShowTableView] = useState(false)
	const isMobile = useIsMobile()

	const allStudents = useMemo(
		() => studentsStore.students || [],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[studentsStore.students.length],
	)

	const activeTab = useMemo(() => {
		if (activeGrade == null || activeGrade === 'all') return 'all'
		if (typeof activeGrade === 'number') return `grade-${activeGrade}`
		const value = String(activeGrade)
		if (value.startsWith('grade-') || value.startsWith('list:')) return value
		if (!Number.isNaN(Number(value))) return `grade-${Number(value)}`
		return value
	}, [activeGrade])

	const tabs = useMemo(() => {
		const gradeCounts = new Map()
		allStudents.forEach(student => {
			const grade = Number(student?.grade)
			if (!Number.isFinite(grade)) return
			gradeCounts.set(grade, (gradeCounts.get(grade) || 0) + 1)
		})

		const gradeTabs = Array.from(gradeCounts.entries())
			.sort((a, b) => a[0] - b[0])
			.map(([grade, count]) => ({
				id: `grade-${grade}`,
				label: `Grade ${grade}`,
				count,
			}))

		const listTabs = (savedLists || []).map(list => ({
			id: `list:${list.id}`,
			label: list.name || 'Saved list',
			count: Array.isArray(list.studentIds) ? list.studentIds.length : null,
		}))

		return [
			{ id: 'all', label: 'All', count: allStudents.length },
			...gradeTabs,
			...listTabs,
		]
	}, [allStudents, savedLists])

	const baseStudents = useMemo(() => {
		if (activeTab.startsWith('grade-')) {
			const grade = Number(activeTab.replace('grade-', ''))
			return allStudents.filter(student => Number(student?.grade) === grade)
		}

		if (activeTab.startsWith('list:')) {
			const listId = activeTab.substring('list:'.length)
			const list = (savedLists || []).find(
				item => String(item.id) === String(listId),
			)
			const ids = new Set((list?.studentIds || []).map(id => String(id).trim()))
			return allStudents.filter(student => ids.has(String(student?.id).trim()))
		}

		return allStudents
	}, [activeTab, allStudents, savedLists])

	// Filter students: when search empty show a default sample to browse; when typing show search results
	const filteredStudents = useMemo(() => {
		if (!searchQuery.trim()) {
			return baseStudents.slice(0, 30)
		}
		const query = searchQuery.toLowerCase().trim()
		return baseStudents.filter(student => {
			const name = (student.name || '').toLowerCase()
			const grade = String(student.grade || '')
			const section = (student.section || '').toLowerCase()

			return (
				name.includes(query) || grade.includes(query) || section.includes(query)
			)
		})
	}, [baseStudents, searchQuery])

	const showAutocomplete = filteredStudents.length > 0
	const selectableStudents = searchQuery.trim() ? filteredStudents : baseStudents

	const isStudentSelected = useCallback(
		studentId => {
			return selectedStudents.some(
				s => String(s.id).trim() === String(studentId).trim(),
			)
		},
		[selectedStudents],
	)

	const handleSearchChange = useCallback(e => {
		setSearchQuery(e.target.value)
	}, [])

	const handleSelectTab = useCallback(
		tabId => {
			if (tabId === 'all') {
				onSelectList?.(null)
				return
			}
			if (String(tabId).startsWith('grade-')) {
				onSelectList?.(Number(String(tabId).replace('grade-', '')))
				return
			}
			if (String(tabId).startsWith('list:')) {
				onSelectList?.(String(tabId).substring('list:'.length))
			}
		},
		[onSelectList],
	)

	const handleToggleStudent = useCallback(student => {
		studentsStore.toggleStudentSelection(student.id)
	}, [])

	// Handle remove student
	const handleRemoveStudent = useCallback(
		studentId => {
			if (isStudentSelected(studentId)) {
				studentsStore.toggleStudentSelection(studentId)
			}
		},
		[isStudentSelected],
	)

	// Get selected students for display (max 7)
	const displaySelectedStudents = useMemo(() => {
		if (isMobile) {
			return selectedStudents.slice(0, 3)
		} else {
			return selectedStudents.slice(0, 7)
		}
	}, [selectedStudents, isMobile])

	const hasMoreSelected = isMobile
		? selectedStudents.length > 3
		: selectedStudents.length > 7
	const moreCount = isMobile
		? selectedStudents.length - 3
		: selectedStudents.length - 7

	const setShowTableViewFalse = useCallback(() => {
		setShowTableView(false)
	}, [])

	const setShowTableViewTrue = useCallback(() => {
		setShowTableView(true)
	}, [])

	const handleClearSelection = useCallback(() => {
		studentsStore.clearSelection()
	}, [])

	const allVisibleSelected = useMemo(() => {
		if (!selectableStudents.length) return false
		return selectableStudents.every(student => isStudentSelected(student.id))
	}, [isStudentSelected, selectableStudents])

	const handleToggleVisibleSelection = useCallback(() => {
		selectableStudents.forEach(student => {
			const selected = isStudentSelected(student.id)
			if (allVisibleSelected && selected) {
				studentsStore.toggleStudentSelection(student.id)
			}
			if (!allVisibleSelected && !selected) {
				studentsStore.toggleStudentSelection(student.id)
			}
		})
	}, [allVisibleSelected, isStudentSelected, selectableStudents])

	return {
		searchQuery,
		handleSearchChange,
		showTableView,
		setShowTableViewFalse,
		setShowTableViewTrue,
		tabs,
		activeTab,
		handleSelectTab,
		filteredStudents,
		showAutocomplete,
		isStudentSelected,
		handleToggleStudent,
		handleRemoveStudent,
		allVisibleSelected,
		handleToggleVisibleSelection,
		displaySelectedStudents,
		hasMoreSelected,
		moreCount,
		handleClearSelection,
		isMobile,
	}
}
